import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Mail, Send, Clock, CheckCircle2, XCircle, RefreshCw, Eye, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

const fmtDate = (iso) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

export default function AdminDigest() {
  const [schedule, setSchedule] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get("/admin/digest/schedule"),
        api.get("/admin/digest/history", { params: { limit: 30 } }),
      ]);
      setSchedule(s.data);
      setHistory(h.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      if (data.emailed) {
        toast.success(`Digest sent to ${data.recipient}`);
      } else {
        toast.warning(`Digest generated but not emailed: ${data.reason}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSending(false); }
  };

  const openPreview = async () => {
    try {
      const { data } = await api.get("/admin/digest/preview");
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const latest = history[0];

  return (
    <div data-testid="admin-digest-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-digest-refresh" className="text-white/60 hover:text-[#D4AF37]"><RefreshCw size={14} /></button>
            <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37]"><ArrowLeft size={14} /> Back to Dashboard</Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">FLEET · Weekly Digest</div>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-4xl md:text-5xl" style={{ fontWeight: 300 }}>
            Weekly <span className="italic text-[#D4AF37]">Digest</span>
          </h1>
          <div className="flex gap-2">
            <button
              onClick={openPreview}
              data-testid="admin-digest-preview"
              className="inline-flex items-center gap-1.5 border border-white/15 text-white rounded px-4 py-2 text-xs tracking-widest hover:border-[#D4AF37]/40"
            ><Eye size={13} /> PREVIEW</button>
            <button
              onClick={sendNow}
              disabled={sending}
              data-testid="admin-digest-send-now"
              className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#050505] font-semibold rounded px-4 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
            ><Send size={13} /> {sending ? "SENDING…" : "SEND DIGEST NOW"}</button>
          </div>
        </div>

        {/* Schedule card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg p-5">
            <div className="flex items-center gap-2 text-[#D4AF37] text-[10px] tracking-widest mb-2"><Clock size={12} /> SCHEDULE</div>
            <div className="text-white font-display text-xl mb-1" style={{ fontWeight: 400 }}>Mondays · 08:00</div>
            <div className="text-xs text-white/60">{schedule?.timezone || "—"}</div>
            <div className="text-xs text-white/40 mt-2">
              Next run: {schedule?.next_run ? fmtDate(schedule.next_run) : "—"}
            </div>
            <div className="text-xs text-white/40 mt-1">
              Status: {schedule?.running ? <span className="text-emerald-400">running</span> : <span className="text-red-400">stopped</span>}
            </div>
          </div>
          <div className="border border-white/10 rounded-lg p-5 bg-[#0a0a0a]">
            <div className="flex items-center gap-2 text-white/60 text-[10px] tracking-widest mb-2"><Mail size={12} /> RECIPIENT</div>
            <div className="text-white text-lg font-mono">{latest?.recipient || "—"}</div>
            <div className="text-xs text-white/40 mt-3">
              Change via <code className="text-[#D4AF37]">RECIPIENT_EMAIL</code> in backend/.env
            </div>
          </div>
          <div className="border border-white/10 rounded-lg p-5 bg-[#0a0a0a]">
            <div className="text-[10px] tracking-widest text-white/50 mb-2">LATEST DIGEST</div>
            {latest ? (
              <>
                <div className="text-white font-display text-xl" style={{ fontWeight: 300 }}>
                  {latest.total_issues} items
                </div>
                <div className="text-xs text-white/50 mt-1">{fmtDate(latest.generated_at)}</div>
                <div className={`text-xs mt-2 ${latest.emailed ? "text-emerald-400" : "text-red-300"}`}>
                  {latest.emailed ? <>✓ Emailed</> : <>✗ Not sent: {latest.reason?.slice(0, 60)}…</>}
                </div>
              </>
            ) : (
              <div className="text-white/40 text-sm">No digests sent yet.</div>
            )}
          </div>
        </div>

        {/* History */}
        <h2 className="text-white/80 tracking-wide text-sm mb-3">HISTORY</h2>
        {loading ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : history.length === 0 ? (
          <div className="border border-white/10 border-dashed rounded-lg p-10 text-center text-white/40 text-sm">
            No digests yet — click "SEND DIGEST NOW" above to fire one.
          </div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-white/5 text-[10px] tracking-widest text-white/50">
                  <th className="text-left px-4 py-3">GENERATED</th>
                  <th className="text-right px-4 py-3">OVERDUE SVC</th>
                  <th className="text-right px-4 py-3">DAMAGE</th>
                  <th className="text-right px-4 py-3">TOLLS</th>
                  <th className="text-right px-4 py-3">EVENTS</th>
                  <th className="text-right px-4 py-3">OVERDUE PAY</th>
                  <th className="text-right px-4 py-3">TOTAL</th>
                  <th className="text-left px-4 py-3">DELIVERY</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} className="border-t border-white/10">
                    <td className="px-4 py-3 text-white/80">{fmtDate(r.generated_at)}</td>
                    <td className="px-4 py-3 text-right">{r.counts?.overdue_services ?? 0}</td>
                    <td className="px-4 py-3 text-right">{r.counts?.pending_damage ?? 0}</td>
                    <td className="px-4 py-3 text-right">{r.counts?.outstanding_tolls ?? 0}</td>
                    <td className="px-4 py-3 text-right">{r.counts?.unack_events ?? 0}</td>
                    <td className="px-4 py-3 text-right">{r.counts?.overdue_payments ?? 0}</td>
                    <td className="px-4 py-3 text-right text-white">{r.total_issues}</td>
                    <td className="px-4 py-3">
                      {r.emailed ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                          <CheckCircle2 size={11} /> Emailed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-300 text-xs" title={r.reason || ""}>
                          <XCircle size={11} /> {r.reason?.slice(0, 40) || "Not sent"}
                          {r.reason?.length > 40 ? "…" : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {preview && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
          className="fixed inset-0 bg-black/80 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
        >
          <div data-testid="admin-digest-preview-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <div className="text-[#D4AF37] text-[10px] tracking-widest">PREVIEW</div>
                <div className="font-display text-lg">Email preview</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-white/60 hover:text-white"><X size={18} /></button>
            </div>
            <iframe
              title="Digest preview"
              srcDoc={preview.html}
              className="flex-1 w-full bg-white"
              style={{ minHeight: "600px", border: 0 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
